/**
 * GSTFilingPage — GSTR-1 & GSTR-3B export tool (Sprint 2026-09).
 *
 * Picks a month, fetches the aggregated report from the backend, and lets
 * the user:
 *   • See a readable summary of each section (B2B, B2CS, HSN, CDN, docs).
 *   • Download the raw JSON so their CA can upload it / paste values into
 *     the GST portal directly.
 *
 * Design notes
 * ------------
 * • Two tabs — GSTR-1 (outward supplies) and GSTR-3B (monthly summary).
 * • Month selector defaults to the LAST completed month (that's what
 *   the user is filing right now; the current month is still being lived).
 * • Each numeric total in the summary is tabular-nums + right-aligned so
 *   the CA's eye can validate at a glance.
 * • Empty months show a clean "No taxable outward supplies this month"
 *   card instead of empty tables.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    FileSpreadsheet,
    Download,
    ChevronLeft,
    Loader2,
    ShieldCheck,
    Building2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { PageHeader } from '../../components/PageHeader';
import { api, fmtMoney } from '../../lib/api';

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------
function lastCompletedMonth() {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}

function monthLabel(ym) {
    if (!ym) return '';
    const [y, m] = ym.split('-');
    const names = ['January', 'February', 'March', 'April', 'May', 'June',
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return `${names[Number(m) - 1] || m} ${y}`;
}

function downloadJson(obj, filename) {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ---------------------------------------------------------------
// Main
// ---------------------------------------------------------------
export default function GSTFilingPage() {
    const [month, setMonth] = useState(lastCompletedMonth());
    const [tab, setTab] = useState('gstr1');
    const [gstr1, setGstr1] = useState(null);
    const [gstr3b, setGstr3b] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch both in parallel — cheap enough on the backend.
            const [r1, r3b] = await Promise.all([
                api.invGSTR1(month, 'summary'),
                api.invGSTR3B(month),
            ]);
            setGstr1(r1);
            setGstr3b(r3b);
        } catch (e) {
            toast.error('Failed to build GST report', {
                description: e?.response?.data?.detail || String(e),
            });
            setGstr1(null);
            setGstr3b(null);
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6" data-testid="gst-filing-page">
            <div className="mb-4">
                <Link to="/invoicing" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <ChevronLeft size={14} /> Back to Invoicing
                </Link>
            </div>
            <PageHeader
                icon={FileSpreadsheet}
                title="GST Filing helper"
                subtitle="Export a GSTR-1 or GSTR-3B ready summary for any month. Your CA can paste the values into the GST portal or upload the JSON directly."
            />

            {/* Month picker */}
            <Card className="p-3 sm:p-4 mb-6">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[200px] max-w-xs">
                        <Label className="text-xs text-muted-foreground">Return period (month)</Label>
                        <Input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            data-testid="gst-month-picker"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        disabled={loading}
                        data-testid="gst-refresh-btn"
                    >
                        {loading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                        Refresh
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!gstr1 || loading}
                            onClick={() => downloadJson(gstr1, `GSTR1-${month}.json`)}
                            data-testid="gst-download-gstr1-btn"
                        >
                            <Download size={14} className="mr-1.5" />
                            Download GSTR-1 JSON
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!gstr3b || loading}
                            onClick={() => downloadJson(gstr3b, `GSTR3B-${month}.json`)}
                            data-testid="gst-download-gstr3b-btn"
                        >
                            <Download size={14} className="mr-1.5" />
                            Download GSTR-3B JSON
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Content */}
            {loading ? (
                <div className="space-y-3">
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            ) : (
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList data-testid="gst-tabs" className="mb-4">
                        <TabsTrigger value="gstr1" data-testid="gst-tab-gstr1">GSTR-1 — Outward supplies</TabsTrigger>
                        <TabsTrigger value="gstr3b" data-testid="gst-tab-gstr3b">GSTR-3B — Monthly summary</TabsTrigger>
                    </TabsList>
                    <TabsContent value="gstr1">
                        <GSTR1View data={gstr1} month={month} />
                    </TabsContent>
                    <TabsContent value="gstr3b">
                        <GSTR3BView data={gstr3b} month={month} />
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
}

// ---------------------------------------------------------------
// GSTR-1 view
// ---------------------------------------------------------------
function GSTR1View({ data, month }) {
    if (!data) return null;
    const empty = data.totals.invoice_count === 0
        && data.totals.credit_note_count === 0
        && data.totals.debit_note_count === 0;

    if (empty) {
        return (
            <Card className="p-10 text-center" data-testid="gstr1-empty">
                <ShieldCheck size={32} className="mx-auto text-muted-foreground mb-2" />
                <h3 className="font-semibold">No outward supplies for {monthLabel(month)}</h3>
                <p className="text-sm text-muted-foreground mt-1">You still need to file a NIL GSTR-1 for this month.</p>
            </Card>
        );
    }

    return (
        <div className="space-y-4" data-testid="gstr1-view">
            {/* Totals card */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Building2 size={16} /> Totals — {monthLabel(month)}
                    </CardTitle>
                    <CardDescription>Snapshot of everything included in the GSTR-1 for this period.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <TotalTile label="Invoices" value={data.totals.invoice_count} />
                        <TotalTile label="Credit notes" value={data.totals.credit_note_count} />
                        <TotalTile label="Debit notes" value={data.totals.debit_note_count} />
                        <TotalTile label="Taxable value" value={fmtMoney(data.totals.total_taxable)} />
                        <TotalTile label="CGST" value={fmtMoney(data.totals.total_cgst)} />
                        <TotalTile label="SGST" value={fmtMoney(data.totals.total_sgst)} />
                        <TotalTile label="IGST" value={fmtMoney(data.totals.total_igst)} />
                        <TotalTile label="Invoice value" value={fmtMoney(data.totals.total_invoice_value)} highlight />
                    </div>
                </CardContent>
            </Card>

            {/* B2B section */}
            <SectionTable
                title="B2B — Supplies to registered persons"
                subtitle="Invoices where the buyer has a GSTIN. Uploaded as Section 4 of GSTR-1."
                rows={data.b2b}
                columns={[
                    { key: 'invoice_number', label: 'Invoice #' },
                    { key: 'invoice_date', label: 'Date' },
                    { key: 'buyer_gstin', label: 'Buyer GSTIN' },
                    { key: 'buyer_name', label: 'Buyer' },
                    { key: 'place_of_supply', label: 'POS' },
                    { key: 'taxable_value', label: 'Taxable', numeric: true, fmt: fmtMoney },
                    { key: 'cgst', label: 'CGST', numeric: true, fmt: fmtMoney },
                    { key: 'sgst', label: 'SGST', numeric: true, fmt: fmtMoney },
                    { key: 'igst', label: 'IGST', numeric: true, fmt: fmtMoney },
                    { key: 'invoice_value', label: 'Total', numeric: true, fmt: fmtMoney },
                ]}
                testid="gstr1-b2b"
            />

            {/* B2CL section */}
            <SectionTable
                title="B2CL — Large inter-state B2C invoices"
                subtitle="Unregistered buyers, inter-state, invoice value > ₹2.5 Lakh. Section 5."
                rows={data.b2cl}
                columns={[
                    { key: 'invoice_number', label: 'Invoice #' },
                    { key: 'invoice_date', label: 'Date' },
                    { key: 'buyer_name', label: 'Buyer' },
                    { key: 'place_of_supply', label: 'POS' },
                    { key: 'taxable_value', label: 'Taxable', numeric: true, fmt: fmtMoney },
                    { key: 'igst', label: 'IGST', numeric: true, fmt: fmtMoney },
                    { key: 'invoice_value', label: 'Total', numeric: true, fmt: fmtMoney },
                ]}
                testid="gstr1-b2cl"
            />

            {/* B2CS section */}
            <SectionTable
                title="B2CS — Small B2C supplies"
                subtitle="Aggregated by place-of-supply and tax rate. Section 7."
                rows={data.b2cs}
                columns={[
                    { key: 'place_of_supply', label: 'POS' },
                    { key: 'gst_rate', label: 'Rate', numeric: true, fmt: (v) => `${v}%` },
                    { key: 'supply_type', label: 'Type' },
                    { key: 'taxable_value', label: 'Taxable', numeric: true, fmt: fmtMoney },
                    { key: 'cgst', label: 'CGST', numeric: true, fmt: fmtMoney },
                    { key: 'sgst', label: 'SGST', numeric: true, fmt: fmtMoney },
                    { key: 'igst', label: 'IGST', numeric: true, fmt: fmtMoney },
                ]}
                testid="gstr1-b2cs"
            />

            {/* CDNR section */}
            <SectionTable
                title="CDNR — Credit / Debit notes to registered persons"
                subtitle="Section 9B."
                rows={data.cdnr}
                columns={[
                    { key: 'invoice_number', label: 'Note #' },
                    { key: 'invoice_date', label: 'Date' },
                    { key: 'note_type', label: 'Type' },
                    { key: 'buyer_gstin', label: 'Buyer GSTIN' },
                    { key: 'taxable_value', label: 'Taxable', numeric: true, fmt: fmtMoney },
                    { key: 'igst', label: 'IGST', numeric: true, fmt: fmtMoney },
                    { key: 'cgst', label: 'CGST', numeric: true, fmt: fmtMoney },
                    { key: 'sgst', label: 'SGST', numeric: true, fmt: fmtMoney },
                    { key: 'invoice_value', label: 'Total', numeric: true, fmt: fmtMoney },
                ]}
                testid="gstr1-cdnr"
            />

            {/* HSN summary */}
            <SectionTable
                title="HSN summary"
                subtitle="Aggregated by HSN + rate + unit. Section 12."
                rows={data.hsn}
                columns={[
                    { key: 'hsn', label: 'HSN' },
                    { key: 'description', label: 'Description' },
                    { key: 'uqc', label: 'UQC' },
                    { key: 'quantity', label: 'Qty', numeric: true },
                    { key: 'gst_rate', label: 'Rate', numeric: true, fmt: (v) => `${v}%` },
                    { key: 'taxable_value', label: 'Taxable', numeric: true, fmt: fmtMoney },
                    { key: 'cgst', label: 'CGST', numeric: true, fmt: fmtMoney },
                    { key: 'sgst', label: 'SGST', numeric: true, fmt: fmtMoney },
                    { key: 'igst', label: 'IGST', numeric: true, fmt: fmtMoney },
                ]}
                testid="gstr1-hsn"
            />

            {/* Docs series */}
            <SectionTable
                title="Document series"
                subtitle="Numbering ranges used this period. Section 13."
                rows={data.docs}
                columns={[
                    { key: 'prefix', label: 'Series' },
                    { key: 'from', label: 'From' },
                    { key: 'to', label: 'To' },
                    { key: 'total', label: 'Total', numeric: true },
                    { key: 'cancelled', label: 'Cancelled', numeric: true },
                ]}
                testid="gstr1-docs"
            />
        </div>
    );
}

// ---------------------------------------------------------------
// GSTR-3B view
// ---------------------------------------------------------------
function GSTR3BView({ data, month }) {
    if (!data) return null;
    return (
        <div className="space-y-4" data-testid="gstr3b-view">
            {/* 3.1 Outward supplies */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">3.1 &nbsp;Outward supplies</CardTitle>
                    <CardDescription>Sum of everything you invoiced during {monthLabel(month)}.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <TotalTile label="Taxable value" value={fmtMoney(data.outward_supplies.taxable)} />
                        <TotalTile label="CGST" value={fmtMoney(data.outward_supplies.cgst)} />
                        <TotalTile label="SGST" value={fmtMoney(data.outward_supplies.sgst)} />
                        <TotalTile label="IGST" value={fmtMoney(data.outward_supplies.igst)} />
                        <TotalTile label="Nil rated / exempt" value={fmtMoney(data.outward_supplies.nil_rated_exempt)} />
                    </div>
                </CardContent>
            </Card>

            {/* 4A ITC */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">4A &nbsp;ITC available</CardTitle>
                    <CardDescription>Computed from your scanned purchase bills with a valid supplier GSTIN.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <TotalTile label="Taxable inward" value={fmtMoney(data.itc_available.taxable)} />
                        <TotalTile label="CGST ITC" value={fmtMoney(data.itc_available.cgst)} />
                        <TotalTile label="SGST ITC" value={fmtMoney(data.itc_available.sgst)} />
                        <TotalTile label="IGST ITC" value={fmtMoney(data.itc_available.igst)} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-2">
                        ITC source: {data.itc_available.source}
                    </div>
                </CardContent>
            </Card>

            {/* 6.1 Net tax */}
            <Card className="border-primary/40 bg-primary/5">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">6.1 &nbsp;Net tax payable</CardTitle>
                    <CardDescription>Outward tax minus ITC — this is what you owe (or lose to ITC carry-forward).</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <TotalTile label="CGST" value={fmtMoney(data.tax_payable.cgst)} />
                        <TotalTile label="SGST" value={fmtMoney(data.tax_payable.sgst)} />
                        <TotalTile label="IGST" value={fmtMoney(data.tax_payable.igst)} />
                        <TotalTile label="Total" value={fmtMoney(data.tax_payable.total)} highlight />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ---------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------
function TotalTile({ label, value, highlight }) {
    return (
        <div
            className={
                'rounded-md border px-3 py-2 ' +
                (highlight ? 'border-primary/50 bg-primary/10' : 'border-border bg-muted/30')
            }
        >
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-semibold tabular-nums mt-0.5">{value}</div>
        </div>
    );
}

function SectionTable({ title, subtitle, rows, columns, testid }) {
    const count = (rows || []).length;
    return (
        <Card data-testid={testid}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="text-base">{title}</CardTitle>
                    {subtitle && <CardDescription className="text-xs">{subtitle}</CardDescription>}
                </div>
                <Badge variant="secondary" className="text-[10px]">{count} rows</Badge>
            </CardHeader>
            <CardContent className="p-0">
                {count === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-6">Nothing for this period.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-muted/40 border-y border-border">
                                    {columns.map((c) => (
                                        <th
                                            key={c.key}
                                            className={
                                                'px-2 py-1.5 font-medium ' +
                                                (c.numeric ? 'text-right' : 'text-left')
                                            }
                                        >
                                            {c.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={i} className="border-b border-border/60 hover:bg-muted/20">
                                        {columns.map((c) => {
                                            const raw = r[c.key];
                                            const shown = c.fmt ? c.fmt(raw ?? '') : (raw ?? '—');
                                            return (
                                                <td
                                                    key={c.key}
                                                    className={
                                                        'px-2 py-1.5 ' +
                                                        (c.numeric ? 'text-right tabular-nums' : 'text-left')
                                                    }
                                                >
                                                    {shown}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
